import useSWRInfinite from 'swr/infinite'
import { API_URL } from '@/utils/constants'
import { strictFetcher } from './strict-fetcher'

/// One row of `GET /alkane/asset/:id/trades` — the same espo page the 24h
/// buy/sell aggregates are computed over, served as rows. `amount` is the
/// asset's leg in whole tokens. The server prices each row and labels where
/// the price came from (`price_source`): espo's own print ("espo" — the
/// historical truth, mint-style rows only), the live price for rows under
/// 48h old ("current"), or the daily candle at the row's own date
/// ("candle"). A row nothing covers keeps nulls and renders "—".
export interface AssetTrade {
	ts: number
	side: 'buy' | 'sell'
	amount: number | null
	price_usd: number | null
	price_source: 'espo' | 'current' | 'candle' | null
	value_usd: number | null
	/// Own-tape rows carry the swap's txid; espo fallback rows don't.
	txid?: string
	/// Executing wallet (the tx's first-input signer), server-derived and
	/// cached. Null/absent when the server couldn't derive it — render "—".
	swapper?: string | null
}

interface TradesBody {
	id: string
	trades: AssetTrade[]
	/// Oldest row's timestamp — the window's honest lower bound. The tape
	/// says "since <this>", never implying completeness beyond it.
	oldest_ts: number | null
	/// Infinite-scroll cursor ("height:seq") when a full page implies more
	/// rows exist below it; absent/null ends the scroll. Espo fallback pages
	/// never carry one.
	next_cursor?: string | null
}

/// The distinction the tape must keep: `unavailable` (upstream down, or an
/// API that predates the endpoint — a 400/404, NOT an empty list) versus a
/// loaded page with zero rows. "No swaps" and "cannot know" are different
/// claims, and the server errors rather than blurring them.
export default function useAssetTrades(assetId: string | null): {
	trades: AssetTrade[] | undefined
	oldestTs: number | null
	unavailable: boolean
	isLoading: boolean
	hasMore: boolean
	loadingMore: boolean
	loadMore: () => void
} {
	// strictFetcher, not the shared lenient one: the server's refusal is a 400
	// WITH a JSON body, and only a thrown error reaches `unavailable`.
	const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<TradesBody>(
		(index, prev) => {
			if (!assetId) {
				return null
			}
			if (index === 0) {
				return `${API_URL}/alkane/asset/${assetId}/trades`
			}
			const cursor = prev?.next_cursor
			if (!cursor) {
				return null
			}
			return `${API_URL}/alkane/asset/${assetId}/trades?before=${encodeURIComponent(cursor)}`
		},
		strictFetcher,
		{
			refreshInterval: 60_000,
			revalidateOnFocus: false,
			// The head page moves with the market; deeper pages are keyed by
			// cursor and immutable — re-fetching them would only jitter.
			revalidateFirstPage: true,
			revalidateAll: false,
			keepPreviousData: false
		}
	)
	const pages = data ?? []
	const last = pages[pages.length - 1]
	const hasMore = Boolean(last?.next_cursor)
	return {
		trades: data ? pages.flatMap((p) => p.trades) : undefined,
		oldestTs: pages[0]?.oldest_ts ?? null,
		unavailable: !!error && !data,
		isLoading,
		hasMore,
		loadingMore: isValidating && size > 1,
		loadMore: () => {
			if (hasMore && !isValidating) {
				void setSize((s) => s + 1)
			}
		}
	}
}
